import { usePerms } from 'contexts/PermsContext';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Collapse } from 'react-bootstrap';
import { IconContext } from 'react-icons';
import { FaChevronDown, FaChevronUp, FaHouse } from 'react-icons/fa6';
import { LiaToggleOffSolid, LiaToggleOnSolid } from 'react-icons/lia';
import { PiVideoFill, PiWrenchFill } from 'react-icons/pi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getLogo } from 'utils/images/image.utils';
import styles from './assets/classicNav.module.css';
import {
  buildSidebarData,
  findOpenDropdownName,
  getEffectiveSelectedPath,
  isDropdownSelected,
} from '../navStateUtils';
/**
 * @param {function} onOpenChanged a funciton listening for when the sidebar open state changes.
 * @param {string} selected the name of the screen that is currently selected. This is so we know which button to highlight as 'selected'
 * @returns
 */
/**
 * The classic desktop sidebar — the navigation as it was before the 2026-09 redesign
 * (75caa0ee3). Shown when the person picks Classic navigation in the display settings.
 */
export default function ClassicSidebar({ onOpenChanged, updateSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openDropdownName, setOpenDropdownName] = useState(null);
  const authContext = useContext(AuthContext);
  const { isAuthenticated } = authContext;

  const perms = usePerms();

  const selectedPage = getEffectiveSelectedPath(location);
  const pageButtons = useMemo(
    () => (isAuthenticated ? buildSidebarData(perms) : []),
    [isAuthenticated, perms, updateSidebar],
  );
  const publicInfoIndex = pageButtons.findIndex((page) => page.name === 'Public Info');
  const primaryPageButtons =
    publicInfoIndex === -1 ? pageButtons : pageButtons.slice(0, publicInfoIndex + 1);
  const secondaryPageButtons = publicInfoIndex === -1 ? [] : pageButtons.slice(publicInfoIndex + 1);

  useEffect(() => {
    if (!isAuthenticated) {
      setOpenDropdownName(null);
      return;
    }

    setOpenDropdownName(findOpenDropdownName(pageButtons, selectedPage));
  }, [isAuthenticated, pageButtons, selectedPage]);

  useEffect(() => {
    if (!isAuthenticated) perms.reset();
  }, [isAuthenticated]);

  const toggleSidebar = () => {
    const nextSidebarOpen = !sidebarOpen;
    if (!nextSidebarOpen) setOpenDropdownName(null);

    onOpenChanged(nextSidebarOpen);
    setSidebarOpen(nextSidebarOpen);
  };

  const closeAllDropdowns = () => {
    setOpenDropdownName(null);
  };

  const toggleDropdown = (dropdownName) => {
    if (!sidebarOpen) {
      onOpenChanged(true);
      setSidebarOpen(true);
      setOpenDropdownName(dropdownName);
      return;
    }

    setOpenDropdownName((currentDropdownName) =>
      currentDropdownName === dropdownName ? null : dropdownName,
    );
  };

  ////////////
  // Styles (used in conditionals DO NOT TOUCH) //
  ////////////

  const ButtonStyleClosed = {
    color: 'var(--color-font)',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: 63,
    alignItems: 'center',
  };

  const ButtonStyleOpen = {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-primary-foreground)',
    '--nav-icon-filter': 'var(--high-contrast-nav-icon-filter, none)',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: 63,
    alignItems: 'center',
  };

  const DropButtonStyleOpen = {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-primary-foreground)',
    '--nav-icon-filter': 'var(--high-contrast-nav-icon-filter, none)',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingLeft: 50,
    height: 63,
    alignItems: 'center',
  };

  const DropButtonStyleSelected = {
    backgroundColor: 'var(--color-primary-highlight)',
    color: 'var(--color-primary-highlight-foreground)',
    '--nav-icon-filter':
      'var(--selected-nav-icon-filter, var(--high-contrast-nav-icon-filter, none))',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingLeft: 50,
    height: 63,
    alignItems: 'center',
  };

  const renderDropdown = (dropdown, index) => {
    if (!dropdown.enabled) return null;

    const dropdownSelected = isDropdownSelected(dropdown, selectedPage);
    const dropdownOpen = sidebarOpen && openDropdownName === dropdown.name;

    return (
      <div key={index}>
        <button
          type="button"
          className={`btn rounded-0 border-0 px-0 ${styles.sectionButton}`}
          style={
            dropdownSelected
              ? { ...DropButtonStyleSelected, paddingLeft: 0 }
              : dropdownOpen
                ? ButtonStyleOpen
                : ButtonStyleClosed
          }
          onClick={() => toggleDropdown(dropdown.name)}
          aria-expanded={dropdownOpen}
        >
          <div
            className={`d-flex w-100 align-items-center ${
              sidebarOpen ? 'justify-content-between' : 'justify-content-center'
            }`}
          >
            <div className="d-flex align-items-center min-w-0">
              <div
                className={`${styles['btn-icon']} ${dropdownSelected ? styles.selectedNavIcon : ''}`}
              >
                {dropdown.icon()}
              </div>
              {sidebarOpen && <div className={styles.menuLabel}>{dropdown.name}</div>}
            </div>
            {sidebarOpen && (
              <div className="me-3">{dropdownOpen ? <FaChevronUp /> : <FaChevronDown />}</div>
            )}
          </div>
        </button>
        <Collapse in={dropdownOpen} mountOnEnter unmountOnExit>
          <div className={`overflow-hidden ${styles.submenuList}`}>
            {dropdown.options.map((option, optionIndex) => {
              if (!option.enabled) return null;

              return (
                <Button
                  className={`${styles.sectionButton} pe-0 py-0 rounded-0 border-0`}
                  key={optionIndex}
                  style={
                    selectedPage === option.href ? DropButtonStyleSelected : DropButtonStyleOpen
                  }
                  onClick={() => {
                    navigate(option.href);
                  }}
                  variant={selectedPage === option.href ? 'primary' : 'secondary'}
                >
                  <div className="d-flex align-items-center">
                    <div
                      className={`${styles['btn-icon']} ${selectedPage === option.href ? styles.selectedNavIcon : ''}`}
                    >
                      {option.icon()}
                    </div>
                    <div className={styles.menuLabel}>{option.name}</div>
                  </div>
                </Button>
              );
            })}
          </div>
        </Collapse>
      </div>
    );
  };

  if (isAuthenticated)
    return (
      <div
        className="fusion-sidebar-shell h-100 overflow-y-auto overflow-x-hidden bg-primary-shadow customScrollbar-Y"
        style={{ width: sidebarOpen ? 300 : 65 }}
      >
        <div
          style={{
            marginBottom: 100,
          }}
        >
          {/* Icon header */}
          <div
            className={`d-flex align-items-center justify-content-center ${styles.brandArea}`}
            style={{
              width: sidebarOpen ? /*259*/ '100%' : 65, //removed the value 259, can put it back if something looks wrong
              height: 107,
            }}
          >
            <Link
              to="/dashboard"
              className="d-flex align-items-center justify-content-center cursor-pointer"
              style={{
                width: 172,
                height: 75,
              }}
              aria-label="Go to dashboard"
              onClick={() => closeAllDropdowns()}
            >
              <img
                className="object-fit-fill"
                style={{ height: sidebarOpen ? '100%' : '75%' }}
                src={getLogo(sidebarOpen)}
                alt="Logo"
              />
            </Link>
          </div>
          {/* Toggle menu */}
          <Button
            className="d-flex w-100 align-items-center rounded-0 px-0"
            style={{ height: 63 }}
            onClick={() => toggleSidebar()}
            variant="dark"
          >
            <div
              className={`d-flex w-100 align-items-center ${
                sidebarOpen ? '' : 'justify-content-center'
              }`}
            >
              <div className={styles['btn-icon']}>
                <IconContext.Provider value={{ size: 30 }}>
                  {sidebarOpen ? <LiaToggleOnSolid /> : <LiaToggleOffSolid />}
                </IconContext.Provider>
              </div>
              {sidebarOpen && <div className={styles.menuLabel}>Toggle Menu</div>}
            </div>
          </Button>
          {/* Home dashboard */}
          <Button
            className="rounded-0 d-flex w-100 align-items-center px-0"
            style={{ height: 63 }}
            onClick={() => {
              closeAllDropdowns();
              navigate('/dashboard');
            }}
            variant={selectedPage === '/dashboard' ? 'primary' : 'dark'}
          >
            <div
              className={`d-flex w-100 align-items-center ${
                sidebarOpen ? '' : 'justify-content-center'
              }`}
            >
              <div className={styles['btn-icon']}>
                <IconContext.Provider value={{ size: 30 }}>
                  <FaHouse />
                </IconContext.Provider>
              </div>
              {sidebarOpen && <div className={styles.menuLabel}>Dashboard</div>}
            </div>
          </Button>

          {primaryPageButtons.map((dropdown, index) => renderDropdown(dropdown, index))}

          {/* ExacqVision VMS */}
          {perms.testPermission(
            [8, 9, 10, 11, 12, 13],
            ['ViewExacqVisionOverwatchVMSConnections'],
          ) && (
            <Button
              className="rounded-0 d-flex w-100 align-items-center px-0"
              style={{ height: 63 }}
              onClick={() => {
                closeAllDropdowns();
                navigate('/vms');
              }}
              variant={selectedPage === '/vms' ? 'primary' : 'dark'}
            >
              <div
                className={`d-flex w-100 align-items-center ${
                  sidebarOpen ? '' : 'justify-content-center'
                }`}
              >
                <div className={styles['btn-icon']}>
                  <IconContext.Provider value={{ size: 35 }}>
                    <PiVideoFill />
                  </IconContext.Provider>
                </div>
                {sidebarOpen && <div className={styles.menuLabel}>Overwatch VMS</div>}
              </div>
            </Button>
          )}

          {/* Maintenance Dashboard*/}
          {perms.maintenanceLevel > 0 && (
            <Button
              className="rounded-0 d-flex w-100 align-items-center px-0"
              style={{ height: 63 }}
              onClick={() => {
                closeAllDropdowns();
                navigate('/maintenancedashboard');
              }}
              variant={selectedPage === '/maintenancedashboard' ? 'primary' : 'dark'}
            >
              <div
                className={`d-flex w-100 align-items-center ${
                  sidebarOpen ? '' : 'justify-content-center'
                }`}
              >
                <div className={styles['btn-icon']}>
                  <IconContext.Provider value={{ size: 35 }}>
                    <PiWrenchFill />
                  </IconContext.Provider>
                </div>
                {sidebarOpen && <div className={styles.menuLabel}>Maintenance Devices</div>}
              </div>
            </Button>
          )}

          {secondaryPageButtons.map((dropdown, index) =>
            renderDropdown(dropdown, primaryPageButtons.length + index),
          )}
        </div>
      </div>
    );
}
